import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS, USER_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';
import type { EndRepresentation } from '~/utils/representation';

// TODO: temporary fix - we need to be able to react on event chains
const LOAN_REQUEST_INVALIDATION_DELAY_MS = 300;

type EndRepresentationMutationProps = {
    onSuccess?: () => void;
    reactivateLeadTrack: boolean;
    userId: string;
};

export const useEndRepresentationMutation = ({
    onSuccess,
    reactivateLeadTrack,
    userId,
}: EndRepresentationMutationProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (representation: EndRepresentation) => {
            const endCommandResult =
                representation.kind === 'delegation'
                    ? await APICommunicator.sendCommand({
                          aggregateId: userId,
                          aggregateName: AGGREGATE_NAMES.USER,
                          commandName: USER_COMMANDS.revokeDelegate,
                          payload: {},
                      })
                    : await APICommunicator.sendCommand({
                          aggregateId: userId,
                          aggregateName: AGGREGATE_NAMES.USER,
                          commandName: USER_COMMANDS.deleteAdviserAbsence,
                          payload: {
                              eventId: representation.eventId,
                          },
                      });

            if (reactivateLeadTrack) {
                // The revokeLimit event has a later position than the end command,
                // so its position is sufficient for the read model to include both.
                return APICommunicator.sendCommand({
                    aggregateId: userId,
                    aggregateName: AGGREGATE_NAMES.USER,
                    commandName: USER_COMMANDS.revokeLimit,
                    payload: {
                        aggregateId: userId,
                    },
                });
            }

            return endCommandResult;
        },
        onError: () => {
            toast.error(t('DASHBOARD.ADVISER_ABSENCE.ERROR'));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.getUser(userId) });

            if (reactivateLeadTrack) {
                // Delayed because the lead-track read models are updated by an event
                // chain that is not reflected in the returned command position.
                setTimeout(() => {
                    queryClient.invalidateQueries({
                        queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getSummary],
                    });
                    queryClient.invalidateQueries({
                        queryKey: [AGGREGATE_NAMES.LOAN_REQUEST, LOAN_REQUEST_RESOLVERS.getCounts],
                    });
                }, LOAN_REQUEST_INVALIDATION_DELAY_MS);
            }
        },
        onSuccess: (data) => {
            if (data?.position) {
                setQueryPosition(JSON.stringify(queryKeys.getUser(userId)), data.position);
            }

            toast.success(t('DASHBOARD.ADVISER_ABSENCE.END_SUCCESS'));
            onSuccess?.();
        },
    });
};
